// İçe aktarma — kaynak dosyanın tarama (scan) beklemesi (2026-10-08, güvenlik/UX taraması ek iş):
// backend `createImportJob` yalnız `scanStatus === "clean"` dosyada iş açar (KPI_INVALID_STATE,
// details.code `scan_pending` / `scan_blocked`). Yükleme adımı commit'ten sonra iş oluşturmadan
// ÖNCE 1 sn aralıkla (en fazla 90 sn) durumu yoklar. Saf: `sleep` ve `getStatus` enjekte edilir
// (period-ui kalıbı, jsdom yok). İç kullanıcı `getKpiEvidence`, portal `getPortalEvidence`
// (`ImportApi.getEvidence`) ile besler.

import type { EvidenceScanStatus } from "@mahir/shared";
import { ApiError } from "../api/client";

export const SCAN_POLL_MS = 1000;
export const SCAN_TIMEOUT_MS = 90_000;

export type ScanWaitResult = "clean" | "blocked" | "error" | "timeout";

/** Tarama durumu → bekle / devam / dur kararı. */
export function scanOutcome(status: EvidenceScanStatus): "wait" | "clean" | "blocked" | "error" {
  if (status === "clean") return "clean";
  if (status === "pending") return "wait";
  if (status === "error") return "error";
  return "blocked"; // infected / quarantined
}

/**
 * `clean` gelene kadar yoklar. Geçici ağ hatası bir sonraki tura bırakılır (zaman aşımı yine
 * işler). `deadline` geçince `timeout`. Dönen değer iş oluşturma kararıdır: yalnız `clean` → createJob.
 */
export async function waitForCleanScan(
  getStatus: () => Promise<EvidenceScanStatus>,
  opts: {
    intervalMs?: number;
    timeoutMs?: number;
    sleep?: (ms: number) => Promise<void>;
    now?: () => number;
  } = {},
): Promise<ScanWaitResult> {
  const intervalMs = opts.intervalMs ?? SCAN_POLL_MS;
  const timeoutMs = opts.timeoutMs ?? SCAN_TIMEOUT_MS;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const now = opts.now ?? (() => Date.now());
  const deadline = now() + timeoutMs;
  for (;;) {
    let status: EvidenceScanStatus | null = null;
    try {
      status = await getStatus();
    } catch {
      // geçici ağ hatası — bir sonraki tur
    }
    if (status) {
      const o = scanOutcome(status);
      if (o !== "wait") return o;
    }
    if (now() >= deadline) return "timeout";
    await sleep(intervalMs);
  }
}

/** createJob yarış durumu: dosya bu arada hâlâ `pending` ise sunucu 4xx KPI_INVALID_STATE/scan_pending döner. */
export function isScanPendingError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.code !== "KPI_INVALID_STATE") return false;
  const d = err.details;
  return !!d && typeof d === "object" && (d as { code?: unknown }).code === "scan_pending";
}

/** createJob: dosya engellendi (infected/quarantined) — iş açılmaz. */
export function isScanBlockedError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.code !== "KPI_INVALID_STATE") return false;
  const d = err.details;
  return !!d && typeof d === "object" && (d as { code?: unknown }).code === "scan_blocked";
}
